import React, { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";
import { fetchWithCache } from "../../CacheDB/cached-Ref-Api";

interface CommunicationTypeData {
  communication_type_code: string;
  disabled: boolean;
}

interface CommunicationTypeDropdownProps {
  selectedCommunicationType: string;
  onCommunicationTypeChange: (
    selectedCommunicationType: string,
    selectedCommunicationTypeObject?: CommunicationTypeData
  ) => void;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const CommunicationTypeDropdown: React.FC<CommunicationTypeDropdownProps> = ({
  selectedCommunicationType,
  onCommunicationTypeChange,
  disabled,
  className = "",
  onFocus,
}) => {
  const [communicationTypeData, setCommunicationTypeData] = useState<CommunicationTypeData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      const startTime = performance.now();
      try {
        const data = await fetchWithCache(
          "communication-type",
          "/ref-data/communication-type"
        );

        setCommunicationTypeData(data);

        datadogRum.addAction("Fetched Communication Types (Cached/Live)", {
          typeCount: data.length,
        });
      } catch (err) {
        setError("Failed to fetch Communication Types");
        datadogRum.addError("Communication Type Fetch Failed", { error: err });
      } finally {
        setLoading(false);
        const endTime = performance.now();
        datadogRum.addAction("Communication Type API Duration", {
          duration: endTime - startTime,
        });
      }
    };

    loadData();
  }, []);

  const options = communicationTypeData.map((type) => ({
    value: type.communication_type_code,
    label: type.communication_type_code,
    disabled: type.disabled,
  }));

  const handleCommunicationTypeChange = (selectedValue: string) => {
    const selected = communicationTypeData.find(
      (item) => item.communication_type_code === selectedValue
    );
    onCommunicationTypeChange(selectedValue, selected);
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedCommunicationType}
      onValueChange={handleCommunicationTypeChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={loading ? "Loading Communication Types..." : error ?? "Communication Type"}
      onFocus={onFocus}
    />
  );
};

export default CommunicationTypeDropdown;
